import React, { useState, useRef, useEffect } from 'react';
import styles from './product-ai-chatbot.scss';

const MAX_UPLOAD_IMAGES = 40;

const STEP = {
    INIT: 'init',
    AWAITING_NAME: 'awaiting_name',
    PRODUCT_OR_SERVICE_SELECT: 'product_or_service_select',
    PROCESSING: 'processing',
    CATEGORY_SELECT: 'category_select',
    DETAILS: 'details',
    IMG_SELECT: 'img_select',
    DONE: 'done',
    AWAITING_MISSING_FIELD: 'awaiting_missing_field',
    BIZ_TYPE_SELECT: 'biz_type_select',
    SAVING: 'saving',
    SAVED: 'saved',
    SAVE_ISSUE: 'save_issue',
    MANDATORY_SPECS: 'mandatory_specs',
};

const BIZ_TYPES = ['Retailer', 'Wholesaler', 'Distributor', 'Exporter', 'Importer', 'Manufacturer'];

let _id = 0;
const uid = () => ++_id;

const guessBrand = (name) => {
    const known = [
        'Apple', 'Samsung', 'OnePlus', 'Xiaomi', 'Realme', 'OPPO', 'Vivo', 'Nokia',
        'Sony', 'LG', 'HP', 'Dell', 'Lenovo', 'Asus', 'Acer', 'Microsoft', 'Google',
        'Motorola', 'Huawei', 'Bosch', 'Philips', 'Whirlpool', 'Havells', 'Bajaj',
        'Crompton', 'Godrej', 'Voltas', 'Daikin', 'Honeywell', 'Panasonic', 'JBL',
        'Bose', 'Noise', 'boAt', 'Nike', 'Adidas', 'Puma', 'Reebok',
    ];
    const lower = name.toLowerCase();
    return known.find(b => lower.includes(b.toLowerCase())) || null;
};

const fmt = (text) => {
    if (!text) return '';
    return String(text)
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n/g, '<br/>');
};

// Specs that shouldn't count toward the description-offer threshold
const EXCLUDED_SPEC_KEYS = new Set([
    'brand','manufacturer','manufactured_details','country_of_origin',
    'importer','importers_detail','price','packaging_length',
    'packaging_weight','packaging_height','packaging_width','expiry_date',
]);

export default function ProductAIChatbot({
    countryList = [],
    bizTypeRequired = false,
    onOpenAddProduct,
    onCategorySelected,
    onStreamDone,
    onFieldFill,
    onSpecConfirm,
    onSpecSuggestionsConfirm,
    onImagesConfirm,
    onFetchDescription,
    onSaveProduct,
    onApplyAndOpen,
    onUploadImageFiles,
    onOpenJdGallery,
}) {
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [step, setStep] = useState(STEP.INIT);
    const [isTyping, setIsTyping] = useState(false);
    const [usedFields, setUsedFields] = useState(new Set());
    const [specSuggestions, setSpecSuggestions] = useState(null);
    const [specSuggestionsFetching, setSpecSuggestionsFetching] = useState(false);
    const [imageList, setImageList] = useState(null); // null=loading, []=none, [...]= images
    const [selectedImgIds, setSelectedImgIds] = useState(new Set());
    const [showAllImages, setShowAllImages] = useState(false);
    const [missingQueue, setMissingQueue] = useState([]);
    const [cardEdits, setCardEdits] = useState({});
    const [brandSuggestions, setBrandSuggestions] = useState([]);
    const [userAddedImages, setUserAddedImages] = useState([]);
    const [urlInputValue, setUrlInputValue] = useState('');
    const [showUrlInput, setShowUrlInput] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [showManualImgUpload, setShowManualImgUpload] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const streamRef = useRef('');
    const stepRef = useRef(step);
    const aiDataRef = useRef(null);
    const streamActiveRef = useRef(false);
    const categoriesShownRef = useRef(false);
    const specDataAfterMapRef = useRef({ mapped: [], unmapped: [] });
    const pendingSpecCountRef = useRef(0);
    const summaryRef = useRef({ productName: '', categoryName: '', specCount: 0, imageCount: 0, images: [], fieldFills: {} });
    const preSummarySpecsRef = useRef([]);
    const mandatorySpecMetaRef = useRef({});
    const onMissingQueueDoneRef = useRef(null);
    const advanceAfterSpecsRef = useRef(null);
    const imageFileInputRef = useRef(null);
    const analysisTimerRef = useRef(null);
    const hasPreloadedSpecsRef = useRef(false);
    const preloadedSpecNamesRef = useRef(new Set());
    const specsFromAIPendingRef = useRef(false);
    const mandatoryUpdatePendingRef = useRef(false);
    const pendingFetchDescriptionRef = useRef(false);
    const fetchAndShowDescriptionRef = useRef(null);
    const pendingSpecSuggestionsRef = useRef(null); // { immediateSpecs, fetchedSpecs: null|[] }
    const showPendingSpecSuggestionsRef = useRef(null);
    stepRef.current = step;
    // Keep ref fresh so event-listener closures ([] deps) can always call the latest version
    advanceAfterSpecsRef.current = (data, cnt) => {
        if (bizTypeRequired && !summaryRef.current.fieldFills['businessType']) {
            pendingSpecCountRef.current = cnt;
            addMsg('bot', 'Please select your **Business Type** to continue:', 'biz_type_dropdown', {
                options: BIZ_TYPES.map(t => ({ label: t, value: t })),
            });
            setStep(STEP.BIZ_TYPE_SELECT);
        } else {
            showDetails(data, cnt);
        }
    };
    showPendingSpecSuggestionsRef.current = () => {
        const pending = pendingSpecSuggestionsRef.current;
        pendingSpecSuggestionsRef.current = null;

        const immediateSpecs = pending?.immediateSpecs || [];
        const fetchedSpecs = pending?.fetchedSpecs; // null = still fetching, [] or [...] = arrived
        const stillFetching = fetchedSpecs === null;

        const allSpecs = (() => {
            if (!fetchedSpecs || !fetchedSpecs.length) return immediateSpecs;
            const taken = new Set(immediateSpecs.map(s => (s.name || '').toLowerCase()));
            return [
                ...immediateSpecs,
                ...fetchedSpecs.filter(s => !taken.has((s.name || '').toLowerCase())),
            ];
        })().map((s, i) => ({ ...s, id: i, selected: s.selected !== false }));

        if (allSpecs.length > 0) {
            const catType = summaryRef.current.categoryType === 'service' ? 'service' : 'product';
            setSpecSuggestions(allSpecs);
            setSpecSuggestionsFetching(stillFetching);
            if (stillFetching) {
                addMsg('bot', `I detected **${immediateSpecs.length}** specification${immediateSpecs.length !== 1 ? 's' : ''} from your ${catType}. Fetching more suggestions...`);
            } else {
                addMsg('bot', `I found **${allSpecs.length}** spec${allSpecs.length !== 1 ? 's' : ''} for your ${catType}. Unselect any you don\'t want, then tap **Continue** to fill them in:`);
            }
        } else if (stillFetching) {
            setIsTyping(true);
        } else {
            // Nothing from any source — advance past spec suggestions
            const { mapped, unmapped } = specDataAfterMapRef.current;
            if (onSpecConfirm) onSpecConfirm(mapped, unmapped);
            const cnt = mapped.filter(s => !EXCLUDED_SPEC_KEYS.has((s.catKey || '').toLowerCase())).length + unmapped.length;
            advanceAfterSpecsRef.current?.(aiDataRef.current, cnt);
        }
    };

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isTyping]);

    // Parent fires this event after it has mapped AI specs → category specs
    useEffect(() => {
        const onSpecsReady = (e) => {
            const { mapped = [], unmapped = [], isUpdate, unfilledMandatory = [] } = e.detail || {};
            showSpecConfirmation(mapped, unmapped, isUpdate, unfilledMandatory);
        };
        window.addEventListener('ai-specs-ready', onSpecsReady);
        return () => window.removeEventListener('ai-specs-ready', onSpecsReady);
    }, []);

    // Parent fires this after fetchAiSpecSuggestions resolves
    useEffect(() => {
        const onSuggestions = (e) => {
            const { specs = [] } = e.detail || {};
            // If mandatory specs haven't been confirmed yet, buffer fetched suggestions.
            // Do NOT clear isTyping here — the typing dots must stay until isUpdate=true
            // fires and renders the mandatory spec card.
            if (mandatoryUpdatePendingRef.current) {
                if (pendingSpecSuggestionsRef.current) {
                    pendingSpecSuggestionsRef.current.fetchedSpecs = specs;
                }
                return;
            }
            setIsTyping(false);
            setSpecSuggestionsFetching(false);

            if (specs.length) {
                // Compute how many are genuinely new (not already in preloaded set)
                const addedCount = specs.filter(
                    s => !preloadedSpecNamesRef.current.has((s.name || '').toLowerCase())
                ).length;

                // Merge fetched suggestions with any AI-stream specs already shown,
                // deduplicating by spec name (case-insensitive).
                setSpecSuggestions(prev => {
                    const prevItems = (prev || []).filter(s => s.fromAIStream);
                    const takenNames = new Set(prevItems.map(s => (s.name || '').toLowerCase()));
                    const newItems = specs
                        .filter(s => !takenNames.has((s.name || '').toLowerCase()))
                        .map((s, i) => ({ ...s, id: prevItems.length + i, selected: true }));
                    return [...prevItems, ...newItems].map((s, idx) => ({ ...s, id: idx }));
                });

                const catType = summaryRef.current.categoryType === 'service' ? 'service' : 'product';
                if (hasPreloadedSpecsRef.current) {
                    if (addedCount > 0) {
                        addMsg('bot', `Found **${addedCount}** more suggestion${addedCount !== 1 ? 's' : ''}. Unselect any you don\'t want, then tap **Continue**.`);
                    } else {
                        addMsg('bot', 'Tap **Continue** to apply the specifications above, or **Skip All** to proceed without them.');
                    }
                } else {
                    addMsg('bot', `I found **${specs.length}** spec${specs.length !== 1 ? 's' : ''} for your ${catType}. Unselect any you don\'t want, then tap **Continue** to fill them in:`);
                }
            } else if (hasPreloadedSpecsRef.current) {
                // No new fetched specs but AI-stream specs are already visible — keep panel open,
                // tell user they can proceed.
                addMsg('bot', 'Tap **Continue** to apply the specifications above, or **Skip All** to proceed without them.');
            } else {
                // Nothing from either source — advance
                const { mapped, unmapped } = specDataAfterMapRef.current;
                if (onSpecConfirm) onSpecConfirm(mapped, unmapped);
                const cnt = mapped.filter(s => !EXCLUDED_SPEC_KEYS.has((s.catKey || '').toLowerCase())).length + unmapped.length;
                advanceAfterSpecsRef.current?.(aiDataRef.current, cnt);
            }
        };
        window.addEventListener('ai-spec-suggestions-ready', onSuggestions);
        return () => window.removeEventListener('ai-spec-suggestions-ready', onSuggestions);
    }, []);

    // Parent fires this after fetching imageListing API for the selected category
    useEffect(() => {
        const onImages = (e) => {
            const { images = [] } = e.detail || {};
            setImageList(images);
        };
        window.addEventListener('ai-images-ready', onImages);
        return () => window.removeEventListener('ai-images-ready', onImages);
    }, []);

    // Parent fires this after fetching brand suggestions for the selected category
    useEffect(() => {
        const onBrands = (e) => {
            const { brands = [] } = e.detail || {};
            if (brands.length) setBrandSuggestions(brands);
        };
        window.addEventListener('ai-brands-ready', onBrands);
        return () => window.removeEventListener('ai-brands-ready', onBrands);
    }, []);

    // ── Auto-greeting ───────────────────────────────────────────────────────
    useEffect(() => {
        let t1, t2;
        t1 = setTimeout(() => {
            addMsg('user', 'Hi');
            setIsTyping(true);
            t2 = setTimeout(() => {
                setIsTyping(false);
                addMsg(
                    'bot',
                    `Hello! I\'m your **AI Assistant**. 👋\n\nShare a product or service name and I\'ll auto-fill the category, brand, specifications, pricing and more directly into the form.\n\nWhat would you like to add?`
                );
                setStep(STEP.AWAITING_NAME);
                setTimeout(() => inputRef.current?.focus(), 200);
            }, 1500);
        }, 600);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, []);

    const addMsg = (from, text, type = 'text', extra = {}) => {
        setMessages(prev => [...prev, { id: uid(), from, type, text, ...extra }]);
    };

    // ── Send ────────────────────────────────────────────────────────────────
    const handleSend = () => {
        const value = inputValue.trim();
        if (!value) return;

        if (step === STEP.AWAITING_MISSING_FIELD) {
            const field = missingQueue[0];
            addMsg('user', value);
            setInputValue('');
            
            if (field === 'Price') {
                const num = String(value).replace(/[^0-9.]/g, '');
                if (!num) {
                    addMsg('bot', `⚠️ Please enter a valid number for the Price.`);
                    return;
                }
                summaryRef.current.fieldFills['price_estimate'] = num;
                if (onFieldFill) onFieldFill('price_estimate', num);
            } else if (field === 'Category') {
                summaryRef.current.categoryName = value;
                addMsg('bot', `Category set to **"${value}"** ✓`);
                if (onCategorySelected) onCategorySelected(value, aiDataRef.current);
                setMissingQueue(missingQueue.slice(1));
                setStep(STEP.DETAILS);
                return;
            } else if (field === 'Product Name') {
                summaryRef.current.productName = value;
                if (onOpenAddProduct) onOpenAddProduct(value);
            } else if (field.startsWith('Spec: ')) {
                const specId = field.replace('Spec: ', '').split('|')[1];
                summaryRef.current.specCount = (summaryRef.current.specCount || 0) + 1;
                if (onFieldFill) onFieldFill(`spec_${specId}`, value);
            } else {
                summaryRef.current.fieldFills[field] = value;
                if (onFieldFill) onFieldFill(field, value);
            }

            const newQ = missingQueue.slice(1);
            setMissingQueue(newQ);
            processMissingQueue(newQ);
            return;
        }

        if (step !== STEP.AWAITING_NAME) return;

        setInputValue('');
        summaryRef.current.productName = value;
        addMsg('user', value);

        if (onOpenAddProduct) onOpenAddProduct(value);

        setStep(STEP.PRODUCT_OR_SERVICE_SELECT);
        setIsTyping(true);
        setTimeout(() => {
            setIsTyping(false);
            addMsg('bot', 'Is this a Product or a Service?', 'product_or_service', {
                options: [
                    { label: 'Product', value: 'product' },
                    { label: 'Service', value: 'service' }
                ]
            });
        }, 500);
    };

    const handleProductOrServiceClick = (type) => {
        if (step !== STEP.PRODUCT_OR_SERVICE_SELECT) return;
        summaryRef.current.categoryType = type;
        addMsg('user', type === 'product' ? 'Product' : 'Service');

        if (onFieldFill) {
            onFieldFill('categoryType', type);
            onFieldFill('type', type.toUpperCase());
        }

        setStep(STEP.PROCESSING);
        setImageList(null);
        setSelectedImgIds(new Set());
        setShowAllImages(false);
        resetSpecSuggestions();
        specsFromAIPendingRef.current = false;
        pendingFetchDescriptionRef.current = false;

        setIsTyping(true);
        const value = summaryRef.current.productName;
        const brand = guessBrand(value);
        const brandHint = (brand && type === 'product') ? `\n\nI can see this is a **${brand}** product — fetching full details now...` : '';

        analysisTimerRef.current = setTimeout(() => {
            analysisTimerRef.current = null;
            setIsTyping(false);
            addMsg('bot', `${type === 'product' ? 'Product' : 'Service'} name set to **"${value}"** ✓${brandHint}\n\nAnalyzing — this will only take a moment...`);
            setIsTyping(true);
        }, 700);

        callAI(value, type);
    };

    // ── AI streaming ────────────────────────────────────────────────────────
    const callAI = async (productName, type) => {
        streamRef.current = '';
        streamActiveRef.current = true;
        categoriesShownRef.current = false;
        try {
            let queryName = productName;
            if (type === 'service' && !queryName.toLowerCase().includes('service')) {
                queryName = queryName + " Service";
            }
            const resp = await fetch('/internal-apis/getDetailsFromAI', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productName: queryName, type }),
            });

            if (!resp.ok) { onAPIError('AI service returned an error.'); return; }

            const reader = resp.body.getReader();
            const decoder = new TextDecoder();

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    if (streamActiveRef.current) onAPIDone(streamRef.current);
                    break;
                }
                const chunk = decoder.decode(value, { stream: true });
                for (const line of chunk.split('\n')) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                        const parsed = JSON.parse(line.slice(6));
                        if (parsed.error) { onAPIError(parsed.error); return; }
                        if (parsed.token) streamRef.current += parsed.token;

                        // Early category detection — fires as soon as categories array closes in stream
                        if (!categoriesShownRef.current) {
                            const arrMatch = streamRef.current.match(/"(?:categories|category)"\s*:\s*\[([^\]]*)\]/);
                            const strArrMatch = !arrMatch && streamRef.current.match(/"(?:categories|category)"\s*:\s*"\[([^\]]*)\]"/);
                            let rawItems = null;
                            if (arrMatch) {
                                rawItems = (arrMatch[1].match(/"([^"]+)"/g) || []).map(s => s.slice(1, -1));
                            } else if (strArrMatch) {
                                rawItems = strArrMatch[1].split(',').map(s => s.replace(/\\"/g, '').trim()).filter(Boolean);
                            }
                            if (rawItems) {
                                const cats = cleanCats(rawItems);
                                if (cats.length) {
                                    categoriesShownRef.current = true;
                                    setIsTyping(false);
                                    addMsg('bot', 'I found the following categories. Which one fits best?', 'category_chips', {
                                        chips: cats.map(c => ({ label: c, value: c })),
                                    });
                                    setStep(STEP.CATEGORY_SELECT);
                                }
                            }
                        }

                        if (parsed.done) { onAPIDone(streamRef.current); return; }
                    } catch { /* skip malformed lines */ }
                }
            }
        } catch {
            onAPIError('Could not reach AI service. You can still fill the form manually.');
        }
    };

    // ── JSON parsing ────────────────────────────────────────────────────────
    const extractBalancedJSON = (text) => {
        const start = text.indexOf('{');
        if (start === -1) return null;
        let depth = 0, inStr = false, esc = false;
        for (let i = start; i < text.length; i++) {
            const ch = text[i];
            if (esc)              { esc = false; continue; }
            if (ch === '\\' && inStr) { esc = true; continue; }
            if (ch === '"')       { inStr = !inStr; continue; }
            if (inStr)            continue;
            if (ch === '{')       depth++;
            else if (ch === '}')  { depth--; if (depth === 0) return text.slice(start, i + 1); }
        }
        return null;
    };

    const extractPartial = (raw) => {
        const str = (key) => { const m = raw.match(new RegExp(`"${key}"\\s*:\\s*"([^"]*)"`)); return m ? m[1] : null; };
        const num = (key) => { const m = raw.match(new RegExp(`"${key}"\\s*:\\s*([0-9]+)`)); return m ? m[1] : null; };
        const arr = (key) => {
            const m = raw.match(new RegExp(`"${key}"\\s*:\\s*\\[([^\\]]*)`));
            if (m) {
                const content = m[1];
                const complete = (content.match(/"([^"]+)"/g) || []).map(s => s.slice(1, -1));
                const trailing = content.match(/,\s*"([^"]+)$/);
                if (trailing) complete.push(trailing[1]);
                return complete;
            }
            // Handle string-encoded array: "key":"[\"item1\",\"item2\"]"
            const sm = raw.match(new RegExp(`"${key}"\\s*:\\s*"\\[([^\\]]*)`));
            if (sm) return sm[1].split(',').map(s => s.replace(/\\"/g, '').trim()).filter(Boolean);
            return [];
        };
        // Extract specs — try multiple possible key names; use a greedy match to handle nested objects
        const specs = {};
        const specsBlockMatch = raw.match(/"(?:specs|specifications|technical_specs|product_specs)"\s*:\s*\{([\s\S]*)/);
        if (specsBlockMatch) {
            // Walk forward to find the balanced closing } of the specs object
            let depth = 0, inStr = false, esc = false;
            let end = -1;
            for (let i = 0; i < specsBlockMatch[1].length; i++) {
                const ch = specsBlockMatch[1][i];
                if (esc) { esc = false; continue; }
                if (ch === '\\' && inStr) { esc = true; continue; }
                if (ch === '"') { inStr = !inStr; continue; }
                if (inStr) continue;
                if (ch === '{') depth++;
                else if (ch === '}') { if (depth === 0) { end = i; break; } depth--; }
            }
            const specsContent = end !== -1 ? specsBlockMatch[1].slice(0, end) : specsBlockMatch[1];
            for (const [, k, v] of specsContent.matchAll(/"([^"]+)"\s*:\s*"([^"]+)"/g)) specs[k] = v;
        }

        // Extract manufacturer from its own block to avoid matching generic "name"/"address" fields
        let mfrName = null, mfrAddr = null;
        const mfrBlock = raw.match(/"manufacturer"\s*:\s*\{([^}]*)/);
        if (mfrBlock) {
            const pairs = {};
            for (const [, k, v] of mfrBlock[1].matchAll(/"([^"]+)"\s*:\s*"([^"]+)"/g)) pairs[k.toLowerCase()] = v;
            mfrName = pairs['name'] || pairs['manufacturer_name'] || pairs['company_name'] || pairs['company'] || null;
            mfrAddr = pairs['address'] || pairs['manufacturer_address'] || pairs['location'] || null;
        } else {
            // Plain string: "manufacturer": "Apple Inc."  or alternate top-level keys
            mfrName = str('manufacturer') || str('manufacturer_name') || str('manufactured_by');
        }

        let priceObj = null;
        const priceBlock = raw.match(/"price_estimate_inr"\s*:\s*\{([^}]*)/);
        if (priceBlock) {
            const minMatch = priceBlock[1].match(/"min"\s*:\s*([0-9]+(?:\.[0-9]+)?)/);
            const maxMatch = priceBlock[1].match(/"max"\s*:\s*([0-9]+(?:\.[0-9]+)?)/);
            if (minMatch || maxMatch) {
                priceObj = { min: minMatch ? minMatch[1] : '', max: maxMatch ? maxMatch[1] : '' };
            }
        }

        let cats = arr('categories');
        if (!cats.length) cats = arr('category');
        if (!cats.length) {
            // Try object-format: "categories": { "key": "value", … }
            const objBlock = raw.match(/"(?:categories|category)"\s*:\s*\{([^}]*)/);
            if (objBlock) {
                cats = [...objBlock[1].matchAll(/"([^"]+)"\s*:\s*"([^"]+)"/g)].map(([, , v]) => v);
            }
        }
        if (!cats.length) {
            const catStr = str('categories') || str('category');
            if (catStr) cats = catStr.split(/[,>]/).map(s => s.trim()).filter(Boolean);
        }
        cats = cleanCats(cats);
        return {
            categories: cats,
            brand:             str('brand'),
            country_of_origin: str('country_of_origin'),
            mrp:               str('mrp') || str('price_estimate') || num('mrp') || num('price_estimate') || str('price_estimate_inr') || num('price_estimate_inr'),
            price_estimate:    priceObj || str('price_estimate') || num('price_estimate') || str('price_estimate_inr') || num('price_estimate_inr'),
            specs,
            manufacturer:      mfrName ? { name: mfrName, address: mfrAddr } : null,
            unit:              str('unit'),
        };
    };

    // Remove AI-generated placeholder keys (category_1, category_2 …), <placeholder> values, and junk entries
    const cleanCats = (arr) => {
        const seen = new Set();
        return arr
            .map(c => (typeof c === 'object' && c !== null
                ? c.name || c.label || c.value || c.title || Object.values(c).find(v => typeof v === 'string') || ''
                : String(c)))
            .flatMap(c => c.split(/[,>]/).map(s => s.trim()))
            .filter(c => {
                const lc = c.toLowerCase();
                return c.length > 1 && !/^category_\d+$/i.test(lc) && !c.startsWith('<') && !seen.has(lc) && seen.add(lc);
            });
    };

    const parseAIResponse = (raw) => {
        const text = raw.replace(/```[\w]*\n?/g, '').replace(/```/g, '').trim();
        // Try balanced JSON first, then try auto-closing a truncated stream
        const jsonStr = extractBalancedJSON(text) || (() => {
            const start = text.indexOf('{');
            if (start === -1) return null;
            // Close any unclosed string + array + object
            let repair = text.slice(start).replace(/,\s*$/, '');
            const open = (repair.match(/\[/g) || []).length - (repair.match(/\]/g) || []).length;
            const inStr = (repair.split('"').length - 1) % 2 === 1;
            if (inStr) repair += '"';
            for (let i = 0; i < open; i++) repair += ']';
            repair += '}';
            try { JSON.parse(repair); return repair; } catch { return null; }
        })();
        if (jsonStr) {
            try {
                const fixed = jsonStr
                    .replace(/,\s*([}\]])/g, '$1')
                    .replace(/"(?:[^"\\]|\\.)*"/g, m =>
                        m.replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/\t/g, '\\t')
                    );
                const parsed = JSON.parse(fixed);

                let catsField = parsed.categories || parsed.category;
                let rawCats = [];
                if (Array.isArray(catsField)) {
                    rawCats = catsField;
                } else if (typeof catsField === 'string') {
                    const catStr = catsField.trim();
                    if (catStr.startsWith('[')) {
                        // AI returned categories as a stringified array e.g. '["Electronics","Mobiles"]'
                        try {
                            const p = JSON.parse(catStr);
                            rawCats = Array.isArray(p) ? p : [catStr];
                        } catch {
                            rawCats = catStr.slice(1, -1).split(',').map(s => s.replace(/"/g, '').trim()).filter(Boolean);
                        }
                    } else {
                        rawCats = [catStr];
                    }
                } else if (catsField && typeof catsField === 'object') {
                    rawCats = Object.values(catsField);
                }

                // Gather any other keys that look like category_1, category_2
                Object.keys(parsed).forEach(k => {
                    if (/^category_\d+$/i.test(k) || /^category\d+$/i.test(k)) {
                        rawCats.push(parsed[k]);
                    }
                });

                parsed.categories = cleanCats(rawCats);

                // Normalise specs — accept alternate key names the AI might use
                if (!parsed.specs || !Object.keys(parsed.specs).length) {
                    const altSpecs = parsed.specifications || parsed.technical_specs || parsed.product_specs || parsed.product_specifications;
                    if (altSpecs && typeof altSpecs === 'object' && !Array.isArray(altSpecs)) {
                        parsed.specs = altSpecs;
                    }
                }

                // Normalize manufacturer to { name, address } regardless of AI format
                const mfr = parsed.manufacturer || parsed.manufacturer_info || parsed.manufactured_by;
                if (typeof mfr === 'string' && mfr) {
                    parsed.manufacturer = { name: mfr, address: null };
                } else if (mfr && typeof mfr === 'object') {
                    const name = mfr.name || mfr.manufacturer_name || mfr.company_name || mfr.company || '';
                    const address = mfr.address || mfr.manufacturer_address || mfr.location || null;
                    parsed.manufacturer = name ? { name, address } : null;
                } else {
                    parsed.manufacturer = null;
                }

                // Handle price estimate object for services
                const priceEst = parsed.price_estimate_inr || parsed.price_estimate;
                if (priceEst && typeof priceEst === 'object' && !Array.isArray(priceEst)) {
                    parsed.price_estimate = {
                        min: String(priceEst.min || priceEst.minimum || '').replace(/[^0-9.]/g, ''),
                        max: String(priceEst.max || priceEst.maximum || '').replace(/[^0-9.]/g, '')
                    };
                }

                return { data: parsed };
            } catch (e) {
                console.warn('[AI chatbot] JSON.parse failed, falling back to partial extraction:', e.message);
            }
        }
        return { data: extractPartial(text) };
    };

    const onAPIDone = (raw) => {
        streamActiveRef.current = false;
        if (analysisTimerRef.current) {
            clearTimeout(analysisTimerRef.current);
            analysisTimerRef.current = null;
        }
        setIsTyping(false);
        try {
            const { data } = parseAIResponse(raw);
            if (!data.price_estimate && data.price_estimate_inr) {
                data.price_estimate = data.price_estimate_inr;
            }
            if (!data.price_estimate && data.mrp) data.price_estimate = typeof data.mrp === 'object' ? data.mrp : String(data.mrp);
            if (!data.mrp && data.price_estimate) data.mrp = typeof data.price_estimate === 'object' ? data.price_estimate : String(data.price_estimate);
            aiDataRef.current = data;
            if (onStreamDone) onStreamDone(data);

            const cats = Array.isArray(data.categories) && data.categories.length ? data.categories : [];
            if (categoriesShownRef.current) {
                // Full parsed data arrived — update early-detected chips in-place with the complete list
                if (cats.length) {
                    setMessages(prev => prev.map(m =>
                        m.type === 'category_chips'
                            ? { ...m, chips: cats.map(c => ({ label: c, value: c })) }
                            : m
                    ));
                }
                return;
            }
            if (cats.length) {
                addMsg('bot', 'I found the following categories. Which one fits best?', 'category_chips', {
                    chips: cats.map(c => ({ label: c, value: c })),
                });
                setStep(STEP.CATEGORY_SELECT);
            } else {
                // No categories from AI — ask user to type one so specs/images can be fetched correctly
                addMsg('bot', 'I couldn\'t detect a category for this product. Please type it below so I can fetch the right specifications and images:');
                const catType = summaryRef.current.categoryType === 'service' ? 'service' : 'product';
                addMsg('bot', `I couldn\'t detect a category for this ${catType}. Please type it below so I can fetch the right specifications and images:`);
                setStep(STEP.AWAITING_MISSING_FIELD);
                setMissingQueue(['Category']);
            }
        } catch (e) {
            console.error('[AI chatbot] parse error:', e.message, '\nRaw (first 400):', raw.slice(0, 400));
            onAPIError('Received an unexpected AI response. Continuing without suggestions.');
            setImageList([]);
            showDetails(null, 0);
        }
    };

    const onAPIError = (msg) => {
        streamActiveRef.current = false;
        if (analysisTimerRef.current) {
            clearTimeout(analysisTimerRef.current);
            analysisTimerRef.current = null;
        }
        setIsTyping(false);
        addMsg('bot', `⚠️ ${msg}`);
        if (stepRef.current === STEP.PROCESSING) setStep(STEP.AWAITING_NAME);
    };

    // ── Category selection ──────────────────────────────────────────────────
    const handleCategoryClick = async (chip) => {
        if (step !== STEP.CATEGORY_SELECT) return;
        addMsg('user', chip.label);
        setIsTyping(true);
        const result = onCategorySelected ? await onCategorySelected(chip.value, aiDataRef.current) : true;
        setIsTyping(false);
        if (result === false) {
            // Category is HARD — remove it from chips and ask user to pick another
            setMessages(prev => prev.map(m =>
                m.type === 'category_chips'
                    ? { ...m, chips: m.chips.filter(c => c.value !== chip.value) }
                    : m
            ));
            addMsg('bot', `⚠️ "${chip.label}" is not available as a category. Please select another one.`);
            return;
        }
        summaryRef.current.categoryName = chip.label;
        setStep(STEP.DETAILS);
        addMsg('bot', `Category set to **"${chip.label}"** ✓`);
    };

    // ── Spec confirmation ───────────────────────────────────────────────────
    const showSpecConfirmation = (mapped, unmapped, isUpdate, unfilledMandatory = []) => {
        if (isUpdate) {
            // Only update the ref when we have real data from getSpecsFromAI
            specDataAfterMapRef.current = { mapped, unmapped };

            // Combine AI-filled mandatory specs + unfilled mandatory specs into one card set
            const mandatoryCards = [
                ...mapped.map(m => ({
                    label:          String(m.catKey || '').toUpperCase() + (String(m.s_unit || '').trim() ? ` (${String(m.s_unit).trim().toUpperCase()})` : ''),
                    value:          m.value,
                    rawValue:       m.value,
                    field:          `spec_${m.sid}`,
                    s_type:         m.s_type         || 'textbox',
                    s_type_options: m.s_type_options  || [],
                    s_regexp:       m.s_regexp        || '',
                    s_unit:         m.s_unit          || '',
                })),
                ...unfilledMandatory.map(s => ({
                    label:          String(s.catKey || s.name || '').toUpperCase() + (String(s.s_unit || '').trim() ? ` (${String(s.s_unit).trim().toUpperCase()})` : ''),
                    value:          '',
                    rawValue:       '',
                    field:          `spec_${s.sid}`,
                    s_type:         s.s_type         || 'textbox',
                    s_type_options: s.s_type_options  || [],
                    s_regexp:       s.s_regexp        || '',
                    s_unit:         s.s_unit          || '',
                })),
            ];

            setIsTyping(false);

            if (mandatoryCards.length > 0) {
                addMsg('bot', 'Please fill in the mandatory specifications below:', 'detail_cards', { cards: mandatoryCards, isMandatoryAI: true });
                summaryRef.current.specCount = (summaryRef.current.specCount || 0) + mapped.length;
            } else {
                // No mandatory specs — unblock and show spec suggestions immediately
                mandatoryUpdatePendingRef.current = false;
                showPendingSpecSuggestionsRef.current?.();
            }

            specsFromAIPendingRef.current = false;
            if (pendingFetchDescriptionRef.current) {
                if (mandatoryCards.length > 0) {
                    setIsTyping(false);
                } else {
                    pendingFetchDescriptionRef.current = false;
                    setTimeout(() => fetchAndShowDescriptionRef.current?.(), 0);
                }
            }
            return;
        }

        // Non-update (initial empty fire from handleAICategorySelected) — don't overwrite
        // specDataAfterMapRef with empty; real data arrives via the isUpdate=true call.

        specsFromAIPendingRef.current = true;

        // Don't overwrite BIZ_TYPE_SELECT — the biz type prompt takes precedence.
        if (stepRef.current === STEP.DONE || stepRef.current === STEP.BIZ_TYPE_SELECT) return;

        setStep(STEP.DETAILS);

        // Block spec suggestions until mandatory specs are shown and confirmed by user
        mandatoryUpdatePendingRef.current = true;

        // Collect AI-stream specs — buffered until mandatory specs are confirmed
        const aiSpecsObj = aiDataRef.current?.specs || {};
        const immediateSpecs = Object.entries(aiSpecsObj)
            .filter(([k, v]) =>
                v && String(v).trim() &&
                !EXCLUDED_SPEC_KEYS.has(k.toLowerCase()) &&
                !isPlaceholder(String(v))
            )
            .map(([k, v], i) => ({
                id: i,
                name: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                value: String(v).trim(),
                unit: '',
                selected: true,
                fromAIStream: true,
            }));

        hasPreloadedSpecsRef.current = immediateSpecs.length > 0;
        preloadedSpecNamesRef.current = new Set(immediateSpecs.map(s => s.name.toLowerCase()));

        // Buffer pending suggestions — shown after mandatory specs are confirmed
        pendingSpecSuggestionsRef.current = { immediateSpecs, fetchedSpecs: null };

        // Show typing indicator while waiting for mandatory specs data from getSpecsFromAI
        setIsTyping(true);
    };

    const toggleSuggestion = (id) =>
        setSpecSuggestions(prev => prev.map(s => s.id === id ? { ...s, selected: !s.selected } : s));

    const resetSpecSuggestions = () => {
        setSpecSuggestions(null);
        setSpecSuggestionsFetching(false);
        hasPreloadedSpecsRef.current = false;
        preloadedSpecNamesRef.current = new Set();
        pendingSpecSuggestionsRef.current = null;
    };

    const handleSuggestionsApply = () => {
        const selected = specSuggestions.filter(s => s.selected);
        const { mapped, unmapped } = specDataAfterMapRef.current;

        if (selected.length && onSpecSuggestionsConfirm) {
            onSpecSuggestionsConfirm(selected);
            addMsg('bot', `✓ Filled **${selected.length}** spec${selected.length !== 1 ? 's' : ''} in the form.`);
        } else {
            addMsg('bot', 'No specs selected — skipped.');
        }

        pendingFetchDescriptionRef.current = false;
        if (onSpecConfirm) onSpecConfirm(mapped, unmapped);
        resetSpecSuggestions();

        const cnt = mapped.filter(s => !EXCLUDED_SPEC_KEYS.has((s.catKey || '').toLowerCase())).length + unmapped.length + selected.length;
        summaryRef.current.specCount = cnt;
        setTimeout(() => advanceAfterSpecsRef.current?.(aiDataRef.current, cnt), 300);
    };

    const handleSuggestionsSkip = () => {
        const { mapped, unmapped } = specDataAfterMapRef.current;
        if (onSpecConfirm) onSpecConfirm(mapped, unmapped);
        resetSpecSuggestions();
        const cnt = mapped.filter(s => !EXCLUDED_SPEC_KEYS.has((s.catKey || '').toLowerCase())).length + unmapped.length;
        summaryRef.current.specCount = cnt;
        setTimeout(() => advanceAfterSpecsRef.current?.(aiDataRef.current, cnt), 300);
    };

    // ── Detail cards ────────────────────────────────────────────────────────
    const isPlaceholder = v => !v || /^<[^>]+>$/.test(String(v).trim()) || String(v).trim() === '';

    const showDetails = (data, specCount = 0) => {
        pendingSpecCountRef.current = specCount;
        if (!data) {
            setStep(STEP.IMG_SELECT);
            return;
        }
        const cards = [];
        const isService = summaryRef.current.categoryType === 'service';

        if (isService) {
            let actualPrice = '';
            let sellingPrice = '';
            let rawPrice = data.price_estimate || data.mrp;
            
            if (rawPrice && typeof rawPrice === 'object') {
                actualPrice = rawPrice.max || '';
                sellingPrice = rawPrice.min || '';
            } else {
                actualPrice = rawPrice && !isPlaceholder(rawPrice) ? String(rawPrice).replace(/[^0-9.]/g, '') : '';
            }

            cards.push({ label: 'Actual Price', value: actualPrice ? `₹ ${actualPrice}` : '', rawValue: actualPrice, field: 'actualPrice' });
            cards.push({ label: 'Discounted Price', value: sellingPrice ? `₹ ${sellingPrice}` : '', rawValue: sellingPrice, field: 'sellingPrice' });
            
            let unit = data.unit || '';
            if (unit && !isPlaceholder(unit)) {
                unit = unit.charAt(0).toUpperCase() + unit.slice(1).toLowerCase();
            } else {
                unit = '';
            }
            cards.push({ 
                label: 'Unit', 
                value: unit, 
                rawValue: unit,
                field: 'unitOfMeasurement', 
                s_type_options: ['Session', 'Visit', 'Hour', 'Day', 'Week', 'Month'] 
            });
        } else {
            if (data.brand && !isPlaceholder(data.brand)) cards.push({ label: 'Brand', value: data.brand, field: 'brand' });
            if (data.country_of_origin && !isPlaceholder(data.country_of_origin)) cards.push({ label: 'Country of Origin', value: data.country_of_origin, field: 'countryOfOrigin' });
            const rawPrice = data.price_estimate || data.mrp;
            if (rawPrice && !isPlaceholder(rawPrice)) {
                const num = String(rawPrice).replace(/[^0-9.]/g, '');
                if (num) cards.push({ label: 'Price Estimate', value: `₹ ${num}`, field: 'price_estimate', rawValue: num });
            }
            if (data.manufacturer?.name && !isPlaceholder(data.manufacturer.name)) cards.push({ label: 'Manufacturer', value: data.manufacturer.name, field: 'manufacturerName' });
            if (data.manufacturer?.address && !isPlaceholder(data.manufacturer.address)) cards.push({ label: 'Manufacturer Address', value: data.manufacturer.address, field: 'manufacturerAddress' });
        }

        if (cards.length) {
            addMsg('bot', 'Here are more details I found. Edit if needed, then click **Update**:', 'detail_cards', { cards });
        }

        setStep(STEP.IMG_SELECT);
    };

    // ── Consolidated summary ─────────────────────────────────────────────────
    const showConsolidatedSummary = () => {
        const s = summaryRef.current;
        const lines = [];
        if (s.productName) lines.push(`**Product:** ${s.productName}`);
        if (s.categoryName) lines.push(`**Category:** ${s.categoryName}`);
        if (s.fieldFills.brand) lines.push(`**Brand:** ${s.fieldFills.brand}`);
        if (s.fieldFills.price_estimate) lines.push(`**Price Estimate:** ₹${s.fieldFills.price_estimate}`);
        else if (s.fieldFills.mrp) lines.push(`**MRP:** ₹${s.fieldFills.mrp}`);
        if (s.fieldFills.countryOfOrigin) lines.push(`**Origin:** ${s.fieldFills.countryOfOrigin}`);
        if (s.specCount > 0) lines.push(`**Specifications:** ${s.specCount} filled`);

        const catType = s.categoryType === 'service' ? 'Service' : 'Product';
        const body = lines.length
            ? `Here\'s a summary of what I\'ve collected:\n\n${lines.join('\n')}\n\nClick **Save ${catType}** to save directly or **Edit ${catType}** to review and edit before saving.`
            : `Your ${catType.toLowerCase()} details are ready. Click **Save ${catType}** to save directly or **Edit ${catType}** to review and edit before saving.`;
        addMsg('bot', body, 'summary', { images: s.images || [] });
    };

    // ── Image selection ─────────────────────────────────────────────────────────
    const finishImageStep = (hadImages = false) => {
        const cnt = pendingSpecCountRef.current;
        const catType = summaryRef.current.categoryType === 'service' ? 'service' : 'product';
        if (!hadImages && cnt === 0) {
            addMsg('bot', `No ${catType} images or additional specifications were found for this category. You can add them manually in the form.`);
        } else if (!hadImages) {
            addMsg('bot', `No ${catType} images found for this category.`);
        }
        fetchAndShowDescription();
    };

    // Auto-generate description, show it, then show summary → DONE
    const fetchAndShowDescription = async () => {
        // Wait until getSpecsFromAI has responded so preSummarySpecsRef is correctly populated
        if (specsFromAIPendingRef.current) {
            setIsTyping(true);
            pendingFetchDescriptionRef.current = true;
            return;
        }
        if (mandatoryUpdatePendingRef.current) {
            setIsTyping(false);
            pendingFetchDescriptionRef.current = true;
            return;
        }
        // Ask user to fill any mandatory specs the AI couldn't determine before showing summary
        if (preSummarySpecsRef.current?.length) {
            setIsTyping(false);
            const queue = preSummarySpecsRef.current.map(s => `Spec: ${s.name}|${s.sid}`);
            // Store full metadata for validation (s_type, s_type_options, s_unit, s_regexp)
            preSummarySpecsRef.current.forEach(s => {
                mandatorySpecMetaRef.current[`Spec: ${s.name}|${s.sid}`] = s;
            });
            preSummarySpecsRef.current = [];
            onMissingQueueDoneRef.current = fetchAndShowDescription;
            setMissingQueue(queue);
            processMissingQueue(queue);
            return;
        }

        setIsTyping(true);
        let text = null;
        try {
            text = onFetchDescription ? await onFetchDescription() : null;
        } catch { /* ignore */ }
        setIsTyping(false);
        if (text) {
            summaryRef.current.description = text;
            const catType = summaryRef.current.categoryType === 'service' ? 'service' : 'product';
            addMsg('bot', `**Best suited description for your ${catType}:**\n\n${text}`);
        }
        showConsolidatedSummary();
        setStep(STEP.DONE);
    };
    fetchAndShowDescriptionRef.current = fetchAndShowDescription;

    const toggleImageSelect = (id) =>
        setSelectedImgIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const clearImageState = () => {
        setImageList(null);
        setSelectedImgIds(new Set());
        setShowAllImages(false);
        setShowManualImgUpload(false);
    };

    const clearUploadState = () => {
        setUserAddedImages([]);
        setUrlInputValue('');
        setShowUrlInput(false);
    };

    const handleImagesApply = () => {
        const selected = (imageList || []).filter(img => selectedImgIds.has(img.id));
        if (selected.length && onImagesConfirm) {
            onImagesConfirm(selected);
            summaryRef.current.imageCount = selected.length;
            summaryRef.current.images = selected.map(img => img.displayUrl);
            addMsg('bot', `✓ **${selected.length}** image${selected.length !== 1 ? 's' : ''} selected.`);
        }
        clearImageState();
        finishImageStep(true);
    };

    const handleImagesSkip = () => {
        clearImageState();
        finishImageStep(true);
    };

    const handleFileInputChange = (e) => {
        const files = Array.from(e.target.files);
        e.target.value = '';
        if (!files.length) return;
        const remaining = MAX_UPLOAD_IMAGES - userAddedImages.length;
        if (remaining <= 0) return;
        const allowed = files.slice(0, remaining);
        setIsUploadingImage(true);
        if (onUploadImageFiles) {
            onUploadImageFiles(allowed, (uploaded) => {
                setIsUploadingImage(false);
                if (uploaded?.length) setUserAddedImages(prev => [...prev, ...uploaded].slice(0, MAX_UPLOAD_IMAGES));
            });
        }
    };

    const removeUserAddedImage = (id) =>
        setUserAddedImages(prev => prev.filter(img => img.id !== id));

    const handleAddImageUrl = () => {
        const url = urlInputValue.trim();
        if (!url) return;
        setUserAddedImages(prev => {
            if (prev.length >= MAX_UPLOAD_IMAGES) return prev;
            return [...prev, { id: `url_${Date.now()}`, displayUrl: url }];
        });
        setUrlInputValue('');
        setShowUrlInput(false);
    };

    const handleUploadPanelDone = () => {
        const imgs = userAddedImages;
        if (imgs.length && onImagesConfirm) {
            onImagesConfirm(imgs);
            summaryRef.current.imageCount = imgs.length;
            summaryRef.current.images = imgs.map(img => img.displayUrl || img.imageUrl);
            addMsg('bot', `✓ **${imgs.length}** image${imgs.length !== 1 ? 's' : ''} added.`);
        }
        clearUploadState();
        finishImageStep(imgs.length > 0);
    };

    const handleUploadPanelSkip = () => {
        clearUploadState();
        finishImageStep(false);
    };

    const handleUseField = (card, currentValue) => {
        summaryRef.current.fieldFills[card.field] = currentValue;
        if (onFieldFill) onFieldFill(card.field, currentValue);
        setUsedFields(prev => new Set([...prev, card.field]));
    };

    const handleSaveAttempt = async () => {
        if (!onSaveProduct) return;
        const result = await onSaveProduct(summaryRef.current.description, () => {
            setStep(STEP.SAVING);
            addMsg('bot', 'We are saving your details...');
            setIsTyping(true);
        });
        setIsTyping(false);
        if (Array.isArray(result) && result.length) {
            setMissingQueue(result);
            processMissingQueue(result);
        } else if (result && result.success && result.issue) {
            setStep(STEP.SAVE_ISSUE);
            const catType = summaryRef.current.categoryType === 'service' ? 'Service' : 'Product';
            addMsg('bot', `⚠️ I found some issue: _"${result.msg}"_\n\nYou need to fix it yourself by clicking the **Edit ${catType}** button below.`);
        } else if (result && result.success) {
            setStep(STEP.SAVED);
            const catType = summaryRef.current.categoryType === 'service' ? 'Service' : 'Product';
            addMsg('bot', `✓ **${result.msg || `${catType} added successfully!`}**`, 'add_more_btn');
        } else {
            setIsTyping(false);
            const errDetail = result?.errorMsg ? `\n\n_Reason: ${result.errorMsg}_` : '';
            const catType = summaryRef.current.categoryType === 'service' ? 'Service' : 'Product';
            addMsg('bot', `⚠️ Couldn't save automatically.${errDetail}\n\nClick **Edit ${catType}** to review and save manually.`);
            setStep(STEP.SAVE_ISSUE);
        }
    };

    const handleBizTypeSelect = (bizType) => {
        if (stepRef.current !== STEP.BIZ_TYPE_SELECT) return;
        addMsg('user', bizType);
        summaryRef.current.fieldFills['businessType'] = bizType;
        if (onFieldFill) onFieldFill('businessType', bizType);

        // If prompted early (after specs), missing queue is empty — proceed to detail cards
        if (!missingQueue.length) {
            showDetails(aiDataRef.current, pendingSpecCountRef.current);
            return;
        }

        const newQ = missingQueue.slice(1);
        setMissingQueue(newQ);
        processMissingQueue(newQ);
    };

    const processMissingQueue = (queue) => {
        if (!queue.length) {
            const cb = onMissingQueueDoneRef.current;
            onMissingQueueDoneRef.current = null;
            if (cb) { cb(); return; }
            handleSaveAttempt();
            return;
        }
        const field = queue[0];
        if (field === 'Product Image' || field === 'Category') {
            const catType = summaryRef.current.categoryType === 'service' ? 'Service' : 'Product';
            addMsg('bot', `**${field}** is required but cannot be added here. Please click **Edit ${catType}** to add it and save.`);
            setStep(STEP.DONE);
            setMissingQueue([]);
        } else if (field === 'BusinessType') {
            addMsg('bot', 'Please select your **Business Type** to continue:', 'biz_type_dropdown', {
                options: BIZ_TYPES.map(t => ({ label: t, value: t })),
            });
            setStep(STEP.BIZ_TYPE_SELECT);
        } else if (field.startsWith('Spec: ')) {
            // Show all pending spec items at once as editable blank cards with validation
            const specItems = queue.filter(f => f.startsWith('Spec: '));
            const remainder = queue.filter(f => !f.startsWith('Spec: '));
            const cards = specItems.map(f => {
                const [name, sid = ''] = f.replace('Spec: ', '').split('|');
                const meta = mandatorySpecMetaRef.current[f] || {};
                const specLabel = String(meta.catKey || name || '').toUpperCase();
                const specUnit = String(meta.s_unit || '').trim().toUpperCase();
                return {
                    label:          specLabel + (specUnit ? ` (${specUnit})` : ''),
                    value:          '',
                    rawValue:       '',
                    field:          `spec_${sid}`,
                    s_type:         meta.s_type         || 'textbox',
                    s_type_options: meta.s_type_options  || [],
                    s_regexp:       meta.s_regexp        || '',
                    s_unit:         meta.s_unit          || '',
                };
            });
            addMsg('bot', 'Please fill in the mandatory specifications below:', 'detail_cards', { cards });
            setStep(STEP.MANDATORY_SPECS);
            setMissingQueue(remainder);
        } else {
            addMsg('bot', `**${field}** is mandatory. Please enter it below:`);
            setStep(STEP.AWAITING_MISSING_FIELD);
            setTimeout(() => inputRef.current?.focus(), 200);
        }
    };

    const resetChatbot = () => {
        setMessages([]);
        setStep(STEP.AWAITING_NAME);
        setInputValue('');
        setIsTyping(false);
        setUsedFields(new Set());
        resetSpecSuggestions();
        setImageList(null);
        setSelectedImgIds(new Set());
        setShowAllImages(false);
        setMissingQueue([]);
        setCardEdits({});
        setBrandSuggestions([]);
        setUserAddedImages([]);
        setUrlInputValue('');
        setShowUrlInput(false);
        setIsUploadingImage(false);
        if (analysisTimerRef.current) {
            clearTimeout(analysisTimerRef.current);
            analysisTimerRef.current = null;
        }
        streamRef.current = '';
        aiDataRef.current = null;
        streamActiveRef.current = false;
        categoriesShownRef.current = false;
        specDataAfterMapRef.current = { mapped: [], unmapped: [] };
        pendingSpecCountRef.current = 0;
        specsFromAIPendingRef.current = false;
        mandatoryUpdatePendingRef.current = false;
        pendingFetchDescriptionRef.current = false;
        pendingSpecSuggestionsRef.current = null;
        summaryRef.current = { productName: '', categoryName: '', specCount: 0, imageCount: 0, images: [], fieldFills: {} };
        preSummarySpecsRef.current = [];
        onMissingQueueDoneRef.current = null;
        setShowManualImgUpload(false);

        setTimeout(() => {
            const catType = summaryRef.current.categoryType === 'service' ? 'service' : 'product';
            addMsg('bot', `What ${catType} would you like to add next?`);
            setTimeout(() => inputRef.current?.focus(), 200);
        }, 300);
    };

    // ── Render helpers ──────────────────────────────────────────────────────
    const renderMsg = (msg) => {

        if (msg.type === 'category_chips') {
            return (
                <div key={msg.id} className={`${styles.msgWrap} ${styles.bot}`}>
                    <div className={`${styles.bubble} ${styles.bot}`}>
                        <p className={styles.msgText} dangerouslySetInnerHTML={{ __html: fmt(msg.text) }} />
                        <div className={styles.chips}>
                            {msg.chips.map((c, i) => (
                                <button
                                    key={i}
                                    className={styles.chip}
                                    onClick={() => handleCategoryClick(c)}
                                    disabled={step !== STEP.CATEGORY_SELECT}
                                >
                                    {c.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }

        if (msg.type === 'product_or_service') {
            const isDisabled = step !== STEP.PRODUCT_OR_SERVICE_SELECT;
            return (
                <div key={msg.id} className={`${styles.msgWrap} ${styles.bot}`}>
                    <div className={`${styles.bubble} ${styles.bot}`}>
                        <p className={styles.msgText} dangerouslySetInnerHTML={{ __html: fmt(msg.text) }} />
                        <div className={styles.chips}>
                            {msg.options.map((o, i) => (
                                <button
                                    key={i}
                                    className={styles.chip}
                                    onClick={() => handleProductOrServiceClick(o.value)}
                                    disabled={isDisabled}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }

        if (msg.type === 'biz_type_dropdown') {
            const isDisabled = step !== STEP.BIZ_TYPE_SELECT;
            return (
                <div key={msg.id} className={`${styles.msgWrap} ${styles.bot}`}>
                    <div className={`${styles.bubble} ${styles.bot}`}>
                        <p className={styles.msgText} dangerouslySetInnerHTML={{ __html: fmt(msg.text) }} />
                        <select
                            className={styles.chatInput}
                            style={{ marginTop: '10px', padding: '8px', width: '100%', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#fff', color: '#000' }}
                            disabled={isDisabled}
                            onChange={(e) => {
                                if (e.target.value) handleBizTypeSelect(e.target.value);
                            }}
                            defaultValue=""
                        >
                            <option value="" disabled>Select Business Type</option>
                            {msg.options.map((o, i) => (
                                <option key={i} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
            );
        }

        if (msg.type === 'detail_cards') {
            const validCards = msg.cards.filter(Boolean);
            const allApplied = validCards.length > 0 && validCards.every(card => usedFields.has(card.field));
            return (
                <div key={msg.id} className={`${styles.msgWrap} ${styles.bot}`}>
                    <div className={`${styles.bubble} ${styles.bot}`}>
                        <p className={styles.msgText} dangerouslySetInnerHTML={{ __html: fmt(msg.text) }} />
                        <div className={styles.detailCards}>
                            {validCards.map((card, i) => {
                                const editKey = `${msg.id}_${i}`;
                                const inputVal = cardEdits[editKey] !== undefined
                                    ? cardEdits[editKey]
                                    : (card.rawValue !== undefined ? String(card.rawValue) : card.value);
                                const isUsed = usedFields.has(card.field);
                                const isCountry = card.field === 'countryOfOrigin';
                                const isBrand = card.field === 'brand';
                                const isSpec = card.field?.startsWith('spec_');
                                const specHasOptions = (isSpec || card.field === 'unitOfMeasurement') && Array.isArray(card.s_type_options) && card.s_type_options.length > 0;
                                const specIsAutosuggest = isSpec && card.s_type === 'dropdownautosuggest';
                                const specDlId = specIsAutosuggest && specHasOptions ? `spec-dl-${editKey}` : undefined;
                                const specRegexp = isSpec && card.s_regexp ? (() => { try { return new RegExp(card.s_regexp); } catch { return null; } })() : null;
                                
                                 let valueToValidate = inputVal;
                                if (isSpec && inputVal) {
                                    const match = String(inputVal).match(/^([0-9]+(?:\.[0-9]+)?)\s*[^0-9.]+$/);
                                    if (match) {
                                        if ((specRegexp && !specRegexp.test(inputVal) && specRegexp.test(match[1])) || card.s_unit) {
                                            valueToValidate = match[1];
                                        }
                                    }
                                }
                                
                                const specInvalid = specRegexp && valueToValidate ? !specRegexp.test(valueToValidate) : false;
                                const listId = isCountry ? `country-dl-${msg.id}` : (isBrand ? `brand-dl-${msg.id}` : undefined);
                                const countryInvalid = isCountry && countryList.length > 0 &&
                                    !countryList.some(c => c.countryName?.toLowerCase() === (inputVal || '').toLowerCase().trim());
                                return (
                                    <div key={i} className={styles.detailCard} style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
                                        <span className={styles.cardLabel} style={{ fontWeight: '600', fontSize: '13px' }}>{card.label}</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                                            <div className={styles.cardInfo} style={{ flex: 1, minWidth: 0 }}>
                                                {isUsed ? (
                                                    <span className={styles.cardValue} title={inputVal} style={{ display: 'block', wordBreak: 'break-word', whiteSpace: 'normal' }}>{inputVal}</span>
                                                ) : specHasOptions && !specIsAutosuggest ? (
                                                    <select
                                                        className={styles.cardInput}
                                                        value={inputVal}
                                                        onChange={e => { const val = e.target.value; setCardEdits(prev => ({ ...prev, [editKey]: val })); }}
                                                        style={{ width: '100%', boxSizing: 'border-box' }}
                                                    >
                                                        <option value="">Select {card.label}...</option>
                                                        {card.s_type_options.map((o, oi) => (
                                                            <option key={oi} value={o}>{o}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <>
                                                        <input
                                                            key={editKey}
                                                            className={styles.cardInput}
                                                            value={inputVal}
                                                            onChange={e => { const val = e.target.value; setCardEdits(prev => ({ ...prev, [editKey]: val })); }}
                                                            list={specDlId || listId}
                                                            placeholder={card.label}
                                                            style={{ width: '100%', boxSizing: 'border-box' }}
                                                        />
                                                        {specIsAutosuggest && specHasOptions && (
                                                            <datalist id={specDlId}>
                                                                {card.s_type_options.map((o, oi) => (
                                                                    <option key={oi} value={o} />
                                                                ))}
                                                            </datalist>
                                                        )}
                                                        {isCountry && countryList.length > 0 && (
                                                            <datalist id={listId}>
                                                                {countryList.map((c, ci) => (
                                                                    <option key={ci} value={c.countryName} />
                                                                ))}
                                                            </datalist>
                                                        )}
                                                        {isBrand && brandSuggestions.length > 0 && (
                                                            <datalist id={listId}>
                                                                {brandSuggestions.map((b, bi) => (
                                                                    <option key={bi} value={b} />
                                                                ))}
                                                            </datalist>
                                                        )}
                                                        {specInvalid && (
                                                            <span style={{ fontSize: '11px', color: '#e53e3e', marginTop: '3px', display: 'block' }}>
                                                                Invalid format
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {validCards.length > 0 && (
                            <div style={{ marginTop: '12px' }}>
                                <button
                                    className={styles.doneBtn}
                                    onClick={() => {
                                        // For mandatory spec cards, all fields must be filled
                                        if (msg.isMandatoryAI) {
                                            const hasBlank = validCards.some((c, idx) => {
                                                const cEditKey = `${msg.id}_${idx}`;
                                                const cVal = cardEdits[cEditKey] !== undefined
                                                    ? cardEdits[cEditKey]
                                                    : (c.rawValue !== undefined ? String(c.rawValue) : c.value);
                                                return !cVal || !String(cVal).trim();
                                            });
                                            if (hasBlank) {
                                                addMsg('bot', '⚠️ Please fill all mandatory specifications before proceeding.');
                                                return;
                                            }
                                        }
                                        validCards.forEach((c, idx) => {
                                            const cEditKey = `${msg.id}_${idx}`;
                                            const cInputVal = cardEdits[cEditKey] !== undefined ? cardEdits[cEditKey] : (c.rawValue !== undefined ? String(c.rawValue) : c.value);
                                            const cIsUsed = usedFields.has(c.field);
                                            const cIsCountry = c.field === 'countryOfOrigin';
                                            const cIsSpec = c.field?.startsWith('spec_');
                                            const cSpecRegexp = cIsSpec && c.s_regexp ? (() => { try { return new RegExp(c.s_regexp); } catch { return null; } })() : null;
                                            
                                            let cValueToSave = cInputVal;
                                            let cValueToValidate = cInputVal;
                                            if (cIsSpec && cInputVal) {
                                                const match = String(cInputVal).match(/^([0-9]+(?:\.[0-9]+)?)\s*[^0-9.]+$/);
                                                if (match) {
                                                    if ((cSpecRegexp && !cSpecRegexp.test(cInputVal) && cSpecRegexp.test(match[1])) || c.s_unit) {
                                                        cValueToSave = match[1];
                                                        cValueToValidate = match[1];
                                                    }
                                                }
                                            }
                                            
                                            const cSpecInvalid = cSpecRegexp && cValueToValidate ? !cSpecRegexp.test(cValueToValidate) : false;
                                            const cCountryInvalid = cIsCountry && countryList.length > 0 && !countryList.some(cl => cl.countryName?.toLowerCase() === (cInputVal || '').toLowerCase().trim());
                                            const cApplyInvalid = cCountryInvalid || cSpecInvalid;

                                            if (!cIsUsed && !cApplyInvalid) {
                                                handleUseField(c, cValueToSave);
                                            }
                                        });

                                        if (msg.isMandatoryAI) {
                                            const allNowApplied = validCards.every(c => summaryRef.current.fieldFills[c.field] !== undefined);
                                            if (allNowApplied) {
                                                mandatoryUpdatePendingRef.current = false;
                                                pendingFetchDescriptionRef.current = false;
                                                showPendingSpecSuggestionsRef.current?.();
                                            }
                                        }
                                    }}
                                    disabled={allApplied}
                                    style={{ padding: '8px 16px', width: 'auto', opacity: allApplied ? 0.6 : 1 }}
                                >
                                    {allApplied ? '✓ Updated' : 'Update'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        if (msg.type === 'add_more_btn') {
            const catType = summaryRef.current.categoryType === 'service' ? 'Service' : 'Product';
            return (
                <div key={msg.id} className={`${styles.msgWrap} ${styles.bot}`}>
                    <div className={`${styles.bubble} ${styles.bot}`}>
                        <p className={styles.msgText} dangerouslySetInnerHTML={{ __html: fmt(msg.text) }} />
                        <div style={{ marginTop: '12px' }}>
                            <button
                                className={styles.doneBtn}
                                onClick={resetChatbot}
                                style={{ padding: '8px 16px', width: 'auto' }}
                            >
                                Add More {catType}
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        if (msg.type === 'summary') {
            const imgs = msg.images || [];
            return (
                <div key={msg.id} className={`${styles.msgWrap} ${styles.bot}`}>
                    <div className={`${styles.bubble} ${styles.bot}`}>
                        <p className={styles.msgText} dangerouslySetInnerHTML={{ __html: fmt(msg.text) }} />
                        {imgs.length > 0 && (
                            <div style={{ marginTop: '10px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: '#738291', marginBottom: '6px' }}>
                                    Selected Images ({imgs.length})
                                </div>
                                <div className={styles.imgGrid}>
                                    {imgs.map((url, i) => (
                                        <div key={i} className={styles.imgThumb} style={{ cursor: 'default' }}>
                                            <img src={url} alt="" loading="lazy" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return (
            <div key={msg.id} className={`${styles.msgWrap} ${styles[msg.from]}`}>
                <div className={`${styles.bubble} ${styles[msg.from]}`}>
                    <p className={styles.msgText} dangerouslySetInnerHTML={{ __html: fmt(msg.text) }} />
                </div>
            </div>
        );
    };

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <div className={styles.chatbot}>
            <div className={styles.chatHeader}>
                <div className={styles.headerAvatar}>
                    <span className="icon-magicWand" />
                </div>
                <div>
                    <div className={styles.headerTitle}>AI Assistant</div>
                    <div className={styles.headerSub}>
                        <span className={styles.onlineDot} />
                        Online
                    </div>
                </div>
            </div>

            <div className={styles.messages}>
                {messages.map(renderMsg)}
                {isTyping && (
                    <div className={`${styles.msgWrap} ${styles.bot}`}>
                        <div className={`${styles.bubble} ${styles.bot} ${styles.typingBubble}`}>
                            <span /><span /><span />
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {[STEP.AWAITING_NAME, STEP.PRODUCT_OR_SERVICE_SELECT, STEP.PROCESSING, STEP.AWAITING_MISSING_FIELD].includes(step) && (
                <div className={styles.inputArea}>
                    <input
                        ref={inputRef}
                        className={styles.chatInput}
                        placeholder={step === STEP.AWAITING_MISSING_FIELD ? "Enter value..." : `Enter ${summaryRef.current.categoryType === 'service' ? 'service' : 'product'} name...`}
                        value={inputValue}
                        onChange={e => [STEP.AWAITING_NAME, STEP.AWAITING_MISSING_FIELD].includes(step) && setInputValue(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSend()}
                        disabled={step === STEP.PROCESSING || step === STEP.PRODUCT_OR_SERVICE_SELECT}
                    />
                    <button
                        className={`${styles.sendBtn} ${step === STEP.PROCESSING ? styles.sendBtnLoading : ''}`}
                        onClick={handleSend}
                        disabled={!inputValue.trim() || step === STEP.PROCESSING || step === STEP.PRODUCT_OR_SERVICE_SELECT}
                    >
                        {step === STEP.PROCESSING
                            ? <span className={styles.sendSpinner} />
                            : <span className="icon-Send" />
                        }
                    </button>
                </div>
            )}

            {specSuggestions?.length > 0 && (
                <div className={styles.suggestPanel}>
                    <div className={styles.suggestTitle}>AI Suggested Specs</div>
                    <div className={styles.suggestList}>
                        {specSuggestions.map(s => (
                            <label key={s.id} className={styles.suggestItem}>
                                <input
                                    type="checkbox"
                                    checked={s.selected}
                                    onChange={() => toggleSuggestion(s.id)}
                                    className={styles.suggestCheck}
                                />
                                <span className={styles.suggestName}>{s.name}</span>
                                <span className={styles.suggestValue}>{s.value}{s.unit ? ` ${s.unit}` : ''}</span>
                            </label>
                        ))}
                    </div>
                    {specSuggestionsFetching ? (
                        <div className={styles.suggestFetching}>
                            <span className={styles.suggestFetchDot} />
                            <span className={styles.suggestFetchDot} />
                            <span className={styles.suggestFetchDot} />
                            <span className={styles.suggestFetchLabel}>Loading more suggestions…</span>
                        </div>
                    ) : (
                        <div className={styles.suggestActions}>
                            <button className={styles.suggestApplyBtn} onClick={handleSuggestionsApply}>
                                Continue
                            </button>
                            <button className={styles.suggestSkipBtn} onClick={handleSuggestionsSkip}>
                                Skip All
                            </button>
                        </div>
                    )}
                </div>
            )}

            {step === STEP.IMG_SELECT && imageList === null && !specSuggestions && (
                <div className={styles.imgLoading}>Finding {summaryRef.current.categoryType === 'service' ? 'service' : 'product'} images...</div>
            )}

            {step === STEP.IMG_SELECT && imageList !== null && imageList.length === 0 && (
                <div className={styles.imgUploadPanel}>
                    <div className={styles.imgTitle}>
                        Add {summaryRef.current.categoryType === 'service' ? 'Service' : 'Product'} Images
                        <span style={{ fontSize: '11px', color: userAddedImages.length >= MAX_UPLOAD_IMAGES ? '#e53e3e' : '#738291', marginLeft: '8px', fontWeight: 400 }}>
                            {userAddedImages.length}/{MAX_UPLOAD_IMAGES}
                        </span>
                    </div>
                    {userAddedImages.length > 0 && (
                        <div className={styles.imgGrid}>
                            {userAddedImages.map(img => (
                                <div key={img.id} className={`${styles.imgThumb} ${styles.imgThumbSelected}`}>
                                    <img src={img.displayUrl} alt="" loading="lazy" />
                                    <button
                                        className={styles.imgRemoveBtn}
                                        onClick={e => { e.stopPropagation(); removeUserAddedImage(img.id); }}
                                    >×</button>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className={styles.imgUploadActions}>
                        <button
                            className={styles.imgUploadActionBtn}
                            onClick={() => imageFileInputRef.current?.click()}
                            disabled={isUploadingImage || userAddedImages.length >= MAX_UPLOAD_IMAGES}
                        >
                            <span className="icon-CameraOutline" />
                            Gallery
                        </button>
                        <button
                            className={styles.imgUploadActionBtn}
                            onClick={() => setShowUrlInput(prev => !prev)}
                            disabled={userAddedImages.length >= MAX_UPLOAD_IMAGES}
                        >
                            <span className="icon-link" />
                            URL
                        </button>
                        <button
                            className={styles.imgUploadActionBtn}
                            onClick={() => onOpenJdGallery && onOpenJdGallery()}
                        >
                            <span className="icon-myImages" />
                            JD Gallery
                        </button>
                    </div>
                    {showUrlInput && (
                        <div className={styles.imgUrlRow}>
                            <input
                                className={styles.chatInput}
                                placeholder="Paste image URL..."
                                value={urlInputValue}
                                onChange={e => setUrlInputValue(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleAddImageUrl()}
                            />
                            <button
                                className={styles.useBtn}
                                onClick={handleAddImageUrl}
                                disabled={!urlInputValue.trim() || userAddedImages.length >= MAX_UPLOAD_IMAGES}
                            >
                                Add
                            </button>
                        </div>
                    )}
                    {isUploadingImage && (
                        <div className={styles.imgLoading}>Uploading...</div>
                    )}
                    <div className={styles.imgActions}>
                        <button
                            className={styles.imgApplyBtn}
                            onClick={handleUploadPanelDone}
                            disabled={!userAddedImages.length}
                        >
                            {userAddedImages.length > 0
                                ? `Add ${userAddedImages.length} Image${userAddedImages.length !== 1 ? 's' : ''}`
                                : 'Done'}
                        </button>
                        <button className={styles.imgSkipBtn} onClick={handleUploadPanelSkip}>
                            Skip
                        </button>
                    </div>
                </div>
            )}

            {step === STEP.IMG_SELECT && imageList?.length > 0 && (
                <div className={styles.imgPanel}>
                    <div className={styles.imgTitle}>Suggested {summaryRef.current.categoryType === 'service' ? 'Service' : 'Product'} Images</div>
                    <div className={styles.imgGrid}>
                        {(showAllImages ? imageList : imageList.slice(0, 4)).map(img => (
                            <div
                                key={img.id}
                                className={`${styles.imgThumb} ${selectedImgIds.has(img.id) ? styles.imgThumbSelected : ''}`}
                                onClick={() => toggleImageSelect(img.id)}
                            >
                                <img src={img.displayUrl} alt="" loading="lazy" />
                                {selectedImgIds.has(img.id) && (
                                    <button
                                        className={styles.imgRemoveBtn}
                                        onClick={e => { e.stopPropagation(); toggleImageSelect(img.id); }}
                                    >×</button>
                                )}
                            </div>
                        ))}
                    </div>
                    {!showAllImages && imageList.length > 4 && (
                        <button className={styles.viewAllBtn} onClick={() => setShowAllImages(true)}>
                            View All ({imageList.length} images)
                        </button>
                    )}
                    {showManualImgUpload && (
                        <>
                            <div className={styles.imgUploadActions}>
                                <button
                                    className={styles.imgUploadActionBtn}
                                    onClick={() => imageFileInputRef.current?.click()}
                                    disabled={isUploadingImage || userAddedImages.length >= MAX_UPLOAD_IMAGES}
                                >
                                    <span className="icon-CameraOutline" />
                                    Gallery
                                </button>
                                <button
                                    className={styles.imgUploadActionBtn}
                                    onClick={() => setShowUrlInput(prev => !prev)}
                                    disabled={userAddedImages.length >= MAX_UPLOAD_IMAGES}
                                >
                                    <span className="icon-link" />
                                    URL
                                </button>
                                <button
                                    className={styles.imgUploadActionBtn}
                                    onClick={() => onOpenJdGallery && onOpenJdGallery()}
                                >
                                    <span className="icon-myImages" />
                                    JD Gallery
                                </button>
                            </div>
                            {showUrlInput && (
                                <div className={styles.imgUrlRow}>
                                    <input
                                        className={styles.chatInput}
                                        placeholder="Paste image URL..."
                                        value={urlInputValue}
                                        onChange={e => setUrlInputValue(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleAddImageUrl()}
                                    />
                                    <button
                                        className={styles.useBtn}
                                        onClick={handleAddImageUrl}
                                        disabled={!urlInputValue.trim() || userAddedImages.length >= MAX_UPLOAD_IMAGES}
                                    >
                                        Add
                                    </button>
                                </div>
                            )}
                            {isUploadingImage && (
                                <div className={styles.imgLoading}>Uploading...</div>
                            )}
                            {userAddedImages.length > 0 && (
                                <div className={styles.imgGrid} style={{ marginTop: '8px' }}>
                                    {userAddedImages.map((img, idx) => (
                                        <div key={idx} className={`${styles.imgThumb} ${styles.imgThumbSelected}`}>
                                            <img src={img.displayUrl || img.imageUrl} alt="" loading="lazy" />
                                            <button
                                                className={styles.imgRemoveBtn}
                                                onClick={e => { e.stopPropagation(); removeUserAddedImage(img.id); }}
                                            >×</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                    <div className={styles.imgActions}>
                        <button
                            className={styles.imgApplyBtn}
                            onClick={() => {
                                if (showManualImgUpload && userAddedImages.length > 0) {
                                    handleUploadPanelDone();
                                } else {
                                    handleImagesApply();
                                }
                            }}
                            disabled={!selectedImgIds.size && !(showManualImgUpload && userAddedImages.length > 0)}
                        >
                            {showManualImgUpload && userAddedImages.length > 0
                                ? `Add ${userAddedImages.length} Image${userAddedImages.length !== 1 ? 's' : ''}`
                                : selectedImgIds.size > 0
                                    ? `Add ${selectedImgIds.size} Image${selectedImgIds.size !== 1 ? 's' : ''}`
                                    : 'Select Images'}
                        </button>
                        {!showManualImgUpload && (
                            <button
                                className={styles.imgUploadActionBtn}
                                onClick={() => setShowManualImgUpload(true)}
                            >
                                Add Manually
                            </button>
                        )}
                        <button className={styles.imgSkipBtn} onClick={handleImagesSkip}>
                            Skip
                        </button>
                    </div>
                </div>
            )}

            {step === STEP.DONE && (
                <div className={styles.doneArea}>
                    <button
                        className={styles.doneBtn}
                        onClick={handleSaveAttempt}
                    >
                        Save {summaryRef.current.categoryType === 'service' ? 'Service' : 'Product'}
                    </button>
                    <button
                        className={styles.editBtn}
                        onClick={() => onApplyAndOpen && onApplyAndOpen(summaryRef.current.description)}
                    >
                        Edit {summaryRef.current.categoryType === 'service' ? 'Service' : 'Product'}
                    </button>
                </div>
            )}

            {step === STEP.SAVE_ISSUE && (
                <div className={styles.doneArea}>
                    <button
                        className={styles.editBtn}
                        onClick={() => onApplyAndOpen && onApplyAndOpen(summaryRef.current.description)}
                    >
                        Edit {summaryRef.current.categoryType === 'service' ? 'Service' : 'Product'}
                    </button>
                </div>
            )}

            {step === STEP.MANDATORY_SPECS && (
                <div className={styles.doneArea}>
                    <button
                        className={styles.doneBtn}
                        onClick={() => {
                            if (missingQueue.length) {
                                processMissingQueue(missingQueue);
                            } else {
                                const cb = onMissingQueueDoneRef.current;
                                onMissingQueueDoneRef.current = null;
                                if (cb) cb();
                                else handleSaveAttempt();
                            }
                        }}
                    >
                        Continue
                    </button>
                </div>
            )}

            <input
                ref={imageFileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
            />
        </div>
    );
}
